import React from 'react';
import cn from 'classnames';
import { IconButton, Group, IconButtonProps, Menu } from '@economic/gaudi';
import { Authorise, AuthorisedLink } from '@economic/react-app';
import LiveChat from '../../shared/components/LiveChat';
import { t } from '../../shared/components/l10n/l10n';
import NotificationsIconButton from './Notifications/Button';
import { NavigationBadge } from './NavigationBadge';
import { InboxIconButton } from './InboxIconButton';
import { ESignatureIconButton } from './ESignatureButton';
import { Trans } from '@lingui/macro';

type IconButtonWithBadge = IconButtonProps & { new?: boolean };

export const IconButtonWithBadge = React.forwardRef(function IconButtonWithBadge(
    props: IconButtonWithBadge,
    ref: React.Ref<HTMLSpanElement>
) {
    const { new: isNew, ...otherProps } = props;

    return (
        <span className="relative" ref={ref}>
            <IconButton {...otherProps} />
            {isNew && <NavigationBadge style={{ top: '-6px', right: '-5px' }} />}
        </span>
    );
});

export const SecondaryNavigation = (props: React.HTMLAttributes<HTMLElement>) => {
    return (
        <Group {...props} as="nav" className={cn('flex h-full items-center', props.className)}>
            <AuthorisedLink to="inbox" as={InboxIconButton} className="wcag-purple-light" />
            <AuthorisedLink
                to="market"
                as={IconButtonWithBadge}
                aria-label="Market"
                className="wcag-purple-light"
                icon="market"
                name="market"
                tooltip={t`HEADER_TAB_MARKET`}
            />
            <AuthorisedLink to="e-signature" as={ESignatureIconButton} className="wcag-purple-light" />
            <Authorise path="user/notifications">
                <NotificationsIconButton className="wcag-purple-light" />
            </Authorise>
            <Authorise path="chat">
                <LiveChat>
                    {({ toggle }) => (
                        <IconButtonWithBadge
                            icon="chat-solid"
                            aria-label="Chat"
                            onClick={toggle}
                            className="wcag-green"
                            name="chat"
                            tooltip={t`HEADER_TAB_LIVECHAT`}
                        />
                    )}
                </LiveChat>
            </Authorise>
            <AuthorisedLink
                to="https://www.e-conomic.dk/support/e-copedia"
                path="external/e-copedia"
                as={IconButtonWithBadge}
                aria-label="e-copedia"
                className="wcag-green"
                icon="e-copedia"
                name="ecopedia"
                target="_blank"
                tooltip="e-copedia"
            />
            <IconButton
                icon="settings-solid"
                aria-label="Settings"
                name="settings"
                className="wcag-red"
                tooltip={t`HEADER_TAB_SETTINGS`}
                menu={menuProps => (
                    <Menu {...menuProps}>
                        <AuthorisedLink to="settings" as={Menu.Link}>
                            <Trans>All settings</Trans>
                        </AuthorisedLink>
                        <AuthorisedLink to="user" as={Menu.Link}>
                            <Trans>My settings</Trans>
                        </AuthorisedLink>
                        <AuthorisedLink to="logout" as={Menu.Link}>
                            <Trans>Log off</Trans>
                        </AuthorisedLink>
                    </Menu>
                )}
            />
        </Group>
    );
};
